import { setCsrfToken } from "@/auth/csrf";
import { apiRequest } from "@/lib/api-request";
import { useAuthStore, type AuthProfile, type SessionEndedReason } from "@/stores/auth-store";

// /auth/me - the only auth status check the SPA ever does. Populates
// the auth store + the CSRF token holder. Called:
//   - on app bootstrap
//   - by the requireAuth route loader (when needed to refresh state)
//   - after a successful refresh (defensively)

interface MeResponse {
  authenticated: boolean;
  profile?: AuthProfile;
  accessTokenExpiresAt?: number;
  sessionExpiresAt?: number | null;
  // Present only when a session that WAS live is gone, and only on the first
  // /auth/me that observes it - the BFF strips the dead credential, so later
  // calls just look anonymous.
  sessionEnded?: SessionEndedReason;
  csrfToken: string;
}

export async function fetchMe(): Promise<MeResponse> {
  const { response } = await apiRequest("auth/me");
  if (!response.ok) {
    throw new Error(`/auth/me returned ${response.status}`);
  }
  return (await response.json()) as MeResponse;
}

export async function bootstrapAuth(): Promise<MeResponse> {
  const me = await fetchMe();
  setCsrfToken(me.csrfToken);
  if (me.authenticated && me.profile && me.accessTokenExpiresAt !== undefined) {
    useAuthStore.getState().setAuthenticated({
      profile: me.profile,
      accessTokenExpiresAt: me.accessTokenExpiresAt,
      sessionExpiresAt: me.sessionExpiresAt ?? null,
    });
  } else {
    useAuthStore.getState().setAnonymous();
  }
  return me;
}
